<template>
<div class="invoices-section">

  <TopBar>
    <template v-slot:left>
      <form class="invoice-search" @submit.prevent="runSearch">
        <input
          v-model="searchTerm"
          type="text"
          class="invoice-search--input"
          placeholder="Invoice number"
          aria-label="Search by invoice number"
        />
        <button type="submit" class="btn btn-sm invoice-search--btn" :disabled="isLoading || !searchTerm">
          <i class="fa fa-search"></i> Search
        </button>
        <span class="invoice-search--hint">Searches all months</span>
      </form>
    </template>
    <template v-slot:right>
      <MonthSelector v-show="!isSearchActive" :minimum-date="new Date(2024, 0, 1)" />
    </template>
  </TopBar>

  <div class="card">
    <div class="card-header">
      <i class="fa fa-spinner fa-spin card-header--icon" v-if="isLoading"></i>
      <i class="fa fa-file-text-o card-header--icon" v-else></i>
      <span class="card-header--title">Invoices</span>
    </div>

    <div class="card-content">

      <p class="invoices-note" v-if="!isSearchActive">
        Invoices and receipts <strong>issued</strong> in this month.
        A document issued this month may refer to a booking from an earlier month.
      </p>

      <div class="invoices-search-chip" v-else>
        Showing results for invoice number <strong>{{ activeSearchTerm }}</strong>
        <button class="invoices-search-chip--clear" @click="clearSearch">
          <i class="fa fa-times"></i> Clear
        </button>
      </div>

      <div class="invoices-error" v-if="errorMessage">
        <i class="fa fa-exclamation-triangle"></i> {{ errorMessage }}
        <button class="invoices-error--retry" @click="reload">Try again</button>
      </div>

      <PaginatedTableComponent
        v-else
        :maxPage="maxPage"
        :data="table.data"
        :columns="table.columns"
        :headings="table.headings"
        :currentPage="currentPage"
        :isLoading="isLoading"
        @changePage="changePage"
      >
        <template #cell-postingDate="{ item }">{{ item.postingDate }}</template>
        <template #cell-invoiceId="{ item }">{{ item.invoiceId }}</template>
        <template #cell-bookingCode="{ item }">{{ item.bookingCode }}</template>
        <template #cell-download="{ item }">
          <div class="invoice-cell">
            <a
              v-if="item.url"
              :href="item.url"
              target="_blank"
              rel="noopener"
              class="btn-invoice"
              :title="item.filename"
            >
              <i class="fa fa-file-pdf-o"></i>
            </a>
            <div v-else class="invoice-empty">—</div>
          </div>
        </template>
      </PaginatedTableComponent>

      <p class="invoices-empty" v-if="!isLoading && !errorMessage && table.data.length === 0">
        No invoices found for this {{ isSearchActive ? 'invoice number' : 'month' }}.
      </p>

    </div>
  </div>
</div>
</template>

<script>
import MonthSelector from '@/components/shared/MonthSelector.vue'
import PaginatedTableComponent from '@/components/shared/PaginatedTableComponent.vue'
import TopBar from '../components/shared/TopBar.vue'
import Api from '@/services/ApiGateway'
import { mapGetters } from 'vuex'

export default {
  components: {
    PaginatedTableComponent,
    MonthSelector,
    TopBar,
  },
  data() {
    return {
      rows: [],
      tableStructure: [],
      invoicesCount: 0,
      currentPage: 1,
      isLoading: false,
      errorMessage: '',
      searchTerm: '',
      activeSearchTerm: '',
      paginationSize: (window.ferryhopper && window.ferryhopper.pagination_size) || 50,
    }
  },
  watch: {
    // Fires on mount and whenever the month changes.
    getActiveDates() {
      if (this.isSearchActive) return
      this.currentPage = 1
      this.loadMonth()
    }
  },
  computed: {
    ...mapGetters({
      getActiveDates: 'datesModule/getActiveDates',
      impersonateUser: 'authentication/impersonateUser',
    }),
    isSearchActive() {
      return this.activeSearchTerm !== ''
    },
    table() {
      return {
        headings: this.tableStructure.map(c => c.columnName),
        columns: this.tableStructure.map(c => c.itemProperty),
        data: this.rows,
      }
    },
    maxPage() {
      if (this.isSearchActive) return 1
      return Math.ceil(this.invoicesCount / this.paginationSize)
    },
  },
  methods: {
    applyResponse(data) {
      this.rows = data.invoices || []
      this.invoicesCount = data.invoicesCount || 0
      this.tableStructure = data.tableStructure || []
    },
    handleError(error) {
      this.rows = []
      this.invoicesCount = 0
      // Never fall back to an empty table on failure: that reads as
      // "you have no invoices" and recreates the problem this page solves.
      this.errorMessage = error?.response?.data?.message
        || 'We could not load your invoices right now. Please try again.'
    },
    loadMonth() {
      this.isLoading = true
      this.errorMessage = ''
      Api.fetchInvoices(this.currentPage, this.impersonateUser)
        .then(response => this.applyResponse(response.data))
        .catch(error => this.handleError(error))
        .finally(() => { this.isLoading = false })
    },
    runSearch() {
      const term = this.searchTerm.trim()
      if (!term) return
      this.currentPage = 1
      this.errorMessage = ''
      // Enter search mode and drop the previous result set in the same tick, so
      // the chip, the empty-state wording and the table can never disagree
      // about which mode the page is in. Clearing the rows is what stops the
      // previous search's results sitting under the new term's chip.
      this.activeSearchTerm = term
      this.rows = []
      this.invoicesCount = 0
      this.isLoading = true
      Api.searchInvoice(term, this.impersonateUser)
        .then(response => this.applyResponse(response.data))
        .catch(error => this.handleError(error))
        .finally(() => { this.isLoading = false })
    },
    clearSearch() {
      this.activeSearchTerm = ''
      this.searchTerm = ''
      this.currentPage = 1
      this.loadMonth()
    },
    reload() {
      this.isSearchActive ? this.runSearch() : this.loadMonth()
    },
    changePage(page) {
      this.currentPage = page
      this.loadMonth()
    },
  },
}
</script>

<style scoped>
.invoices-section {
  padding: 1rem 0 2rem 0;
}

.invoices-note {
  margin: 0 0 1rem 0;
  padding: 0.6rem 0.8rem;
  background: var(--brand-primary-extra-light, #e0faf6);
  border-left: 3px solid var(--brand-primary, #01c3a7);
  border-radius: 3px;
  font-size: 13px;
  color: var(--gray-dark, #464646);
}

.invoices-search-chip {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0 0 1rem 0;
  padding: 0.6rem 0.8rem;
  background: var(--gray-lighter, #f7f7f7);
  border-radius: 3px;
  font-size: 13px;
}

.invoices-search-chip--clear {
  background: none;
  border: none;
  color: var(--brand-primary-dark, #008f79);
  cursor: pointer;
  font-size: 13px;
}

.invoice-search {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.invoice-search--input {
  height: 32px;
  padding: 0 0.6rem;
  border: 1px solid var(--gray-d4, #d4d4d4);
  border-radius: 3px;
  font-size: 13px;
}

.invoice-search--hint {
  font-size: 11px;
  color: var(--gray, #999);
}

.invoices-error {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.8rem;
  background: #fdf0f2;
  border-left: 3px solid var(--brand-warning, #d40027);
  border-radius: 3px;
  font-size: 13px;
}

.invoices-error--retry {
  background: none;
  border: none;
  color: var(--brand-warning, #d40027);
  text-decoration: underline;
  cursor: pointer;
  font-size: 13px;
}

.invoices-empty {
  padding: 1rem 0;
  text-align: center;
  color: var(--gray, #999);
  font-size: 13px;
}

.invoice-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
}

.btn-invoice {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: none;
  border: none;
  border-radius: 4px;
  padding: 0;
  cursor: pointer;
  color: #555;
  font-size: 16px;
  transition: color 0.2s, background 0.2s;
}

.btn-invoice:hover {
  color: #0066cc;
  background: #e8f0fe;
}

.invoice-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  color: #999;
}


</style>
